<template>
  <main class="page-shell">
    <div class="toolbar">
      <el-button size="small" icon="el-icon-back" @click="$router.push('/admin/users')">返回</el-button>
      <span v-if="user" class="page-title">
        {{ user.name || user.username }}（{{ user.username }}）
      </span>
      <div class="toolbar-spacer"></div>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openRecharge">充值 / 续费</el-button>
    </div>

    <template v-if="user">
      <div class="stat-grid section-gap">
        <div class="stat-card">
          <div class="label">当前余额</div>
          <div class="value" :style="{ color: Number(user.balance) > 0 ? 'var(--green)' : 'var(--red)' }">
            {{ formatMoney(user.balance) }} 元
          </div>
        </div>
        <div class="stat-card">
          <div class="label">累计消费</div>
          <div class="value">{{ formatMoney(stats.totalCost) }} 元</div>
        </div>
        <div class="stat-card">
          <div class="label">已配置模型</div>
          <div class="value">{{ stats.modelCount }} 个</div>
        </div>
        <div class="stat-card">
          <div class="label">状态</div>
          <div class="value" style="font-size: 16px">
            <el-tag :type="user.status === 'active' ? 'success' : 'danger'">
              {{ user.status === 'active' ? '可用' : '停用' }}
            </el-tag>
          </div>
        </div>
      </div>

      <section class="soft-card tabs-panel section-gap">
        <el-tabs v-model="tab">
          <!-- 基本信息 -->
          <el-tab-pane label="基本信息" name="base">
            <el-descriptions :column="2" border size="medium">
              <el-descriptions-item label="用户名">{{ user.username }}</el-descriptions-item>
              <el-descriptions-item label="当前余额">{{ formatMoney(user.balance) }} 元</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{ formatTime(user.createdAt) }}</el-descriptions-item>
              <el-descriptions-item label="状态" :span="2">
                <el-tag :type="user.status === 'active' ? 'success' : 'danger'">
                  {{ user.status === 'active' ? '可用' : '停用' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="API Key" :span="2">
                <div class="api-key-box">
                  <code>{{ user.apiKey }}</code>
                  <el-button type="text" icon="el-icon-document-copy" @click="copyKey(user.apiKey)">复制</el-button>
                </div>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <!-- 模型定价 -->
          <el-tab-pane label="模型定价" name="models">
            <div class="toolbar">
              <el-input v-model="modelForm.modelName" size="small" placeholder="模型名 如 deepseek-v4-flash" style="width: 200px" />
              <span class="price-label">输入价</span>
              <el-input-number v-model="modelForm.inputPrice" size="small" :precision="6" :controls="false" style="width: 110px" />
              <span class="price-label">输出价</span>
              <el-input-number v-model="modelForm.outputPrice" size="small" :precision="6" :controls="false" style="width: 110px" />
              <span class="price-label">缓存价</span>
              <el-input-number v-model="modelForm.cachePrice" size="small" :precision="6" :controls="false" style="width: 110px" />
              <el-button type="primary" size="small" icon="el-icon-plus" @click="addModel">添加 / 维护价格</el-button>
              <span class="muted-text">单位：元/1K tokens</span>
            </div>
            <el-table :data="models" v-loading="modelsLoading">
              <el-table-column prop="modelName" label="模型名称" />
              <el-table-column prop="inputPrice" label="输入价" align="right" />
              <el-table-column prop="outputPrice" label="输出价" align="right" />
              <el-table-column prop="cachePrice" label="缓存价" align="right" />
              <el-table-column label="操作" width="100">
                <template slot-scope="s">
                  <el-button size="small" type="text" class="delete-btn" @click="removeModel(s.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- 调用记录 -->
          <el-tab-pane label="调用记录" name="usage">
            <el-table :data="usageList" v-loading="usageLoading">
              <el-table-column prop="modelName" label="模型" />
              <el-table-column label="输入Token" align="right">
                <template slot-scope="s">{{ formatTokens(s.row.inputTokens) }}</template>
              </el-table-column>
              <el-table-column label="缓存Token" align="right">
                <template slot-scope="s">{{ formatTokens(s.row.cacheTokens) }}</template>
              </el-table-column>
              <el-table-column label="输出Token" align="right">
                <template slot-scope="s">{{ formatTokens(s.row.outputTokens) }}</template>
              </el-table-column>
              <el-table-column label="总Token" align="right">
                <template slot-scope="s">{{ formatTokens(s.row.totalTokens) }}</template>
              </el-table-column>
              <el-table-column label="费用(元)" align="right">
                <template slot-scope="s">{{ formatMoney(s.row.totalCost) }}</template>
              </el-table-column>
              <el-table-column label="时间">
                <template slot-scope="s">{{ formatTime(s.row.requestStartAt || s.row.createdAt) }}</template>
              </el-table-column>
            </el-table>
            <el-pagination class="pagination-bar" layout="total, prev, pager, next" :total="usageTotal"
                           :page-size="usageSize" :current-page.sync="usagePage" @current-change="loadUsage" small />
          </el-tab-pane>

          <!-- 充值记录 -->
          <el-tab-pane label="充值记录" name="balanceLogs">
            <el-table :data="balanceList" v-loading="balanceLoading">
              <el-table-column label="充值金额(元)" align="right">
                <template slot-scope="s">
                  <span style="color: var(--green)">{{ formatMoney(s.row.amount) }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="beforeBalance" label="充值前" align="right" />
              <el-table-column prop="afterBalance" label="充值后" align="right" />
              <el-table-column prop="remark" label="备注" />
              <el-table-column label="时间">
                <template slot-scope="s">{{ formatTime(s.row.createdAt) }}</template>
              </el-table-column>
            </el-table>
            <el-pagination class="pagination-bar" layout="total, prev, pager, next" :total="balanceTotal"
                           :page-size="balanceSize" :current-page.sync="balancePage" @current-change="loadBalanceLogs" small />
          </el-tab-pane>
        </el-tabs>
      </section>
    </template>

    <!-- 充值弹窗 -->
    <el-dialog title="充值 / 续费" :visible.sync="showRecharge" width="420px">
      <el-form label-width="80px">
        <el-form-item label="充值金额">
          <el-input-number v-model="rechargeForm.amount" :min="0.01" :precision="2" :controls="false" style="width: 100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="rechargeForm.remark" placeholder="可选" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="showRecharge = false">取消</el-button>
        <el-button size="small" type="primary" :loading="recharging" @click="submitRecharge">确认充值</el-button>
      </div>
    </el-dialog>
  </main>
</template>

<script>
import { formatMoney, formatTime, formatTokens } from '@/utils/format'

export default {
  name: 'AdminUserDetail',
  data() {
    return {
      userId: '',
      user: null,
      models: [],
      modelsLoading: false,
      stats: { totalCost: 0, modelCount: 0 },
      tab: 'base',
      modelForm: { modelName: '', inputPrice: 0, outputPrice: 0, cachePrice: 0 },
      showRecharge: false,
      recharging: false,
      rechargeForm: { amount: 100, remark: '' },
      usageList: [],
      usageTotal: 0,
      usagePage: 1,
      usageSize: 10,
      usageLoading: false,
      balanceList: [],
      balanceTotal: 0,
      balancePage: 1,
      balanceSize: 10,
      balanceLoading: false
    }
  },
  created() {
    this.userId = this.$route.params.id
    this.loadDetail()
  },
  methods: {
    formatMoney,
    formatTime,
    formatTokens,
    async loadDetail() {
      const res = await this.$http.get(`/admin/users/${this.userId}`)
      if (res.code === 200) {
        this.user = res.data.user
        this.models = res.data.models
        this.stats = res.data.stats
      }
      this.loadUsage()
      this.loadBalanceLogs()
    },
    openRecharge() {
      this.rechargeForm = { amount: 100, remark: '' }
      this.showRecharge = true
    },
    async submitRecharge() {
      if (!this.rechargeForm.amount || this.rechargeForm.amount <= 0) {
        this.$message.warning('请输入充值金额')
        return
      }
      this.recharging = true
      try {
        const res = await this.$http.post(`/admin/users/${this.userId}/recharge`, this.rechargeForm)
        if (res.code === 200) {
          this.$message.success('充值成功')
          this.showRecharge = false
          this.loadDetail()
        }
      } catch (e) {
      } finally {
        this.recharging = false
      }
    },
    async addModel() {
      if (!this.modelForm.modelName) {
        this.$message.warning('请输入模型名称')
        return
      }
      const res = await this.$http.post(`/admin/users/${this.userId}/models`, this.modelForm)
      if (res.code === 200) {
        this.$message.success('保存成功')
        this.modelForm = { modelName: '', inputPrice: 0, outputPrice: 0, cachePrice: 0 }
        this.loadDetail()
      }
    },
    removeModel(row) {
      this.$confirm(`删除模型「${row.modelName}」的价格配置？`, '提示', { type: 'warning' }).then(async () => {
        const res = await this.$http.delete(`/admin/users/${this.userId}/models/${row.id}`)
        if (res.code === 200) {
          this.$message.success('已删除')
          this.loadDetail()
        }
      }).catch(() => {})
    },
    async loadUsage() {
      this.usageLoading = true
      try {
        const res = await this.$http.get(`/admin/users/${this.userId}/usage`, {
          params: { page: this.usagePage, size: this.usageSize }
        })
        if (res.code === 200) {
          this.usageList = res.data.list
          this.usageTotal = res.data.total
        }
      } catch (e) {
      } finally {
        this.usageLoading = false
      }
    },
    async loadBalanceLogs() {
      this.balanceLoading = true
      try {
        const res = await this.$http.get(`/admin/users/${this.userId}/balance-logs`, {
          params: { page: this.balancePage, size: this.balanceSize }
        })
        if (res.code === 200) {
          this.balanceList = res.data.list
          this.balanceTotal = res.data.total
        }
      } catch (e) {
      } finally {
        this.balanceLoading = false
      }
    },
    copyKey(key) {
      const el = document.createElement('textarea')
      el.value = key
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      this.$message.success('API Key 已复制')
    }
  }
}
</script>

<style scoped>
.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.price-label {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
</style>